import { useState } from "react";
import { db, doc, updateDoc } from "../../../firebaseConfig";
import { useAtom } from "jotai";
import { themeAtom, currentStaffDataAtom } from "../../../-Atoms";
import EmailModalV2 from "../../../EmailModalV2/EmailModalV2";
import StoryboardDetailsModal from "./StoryboardDetailsModal";

import Dialog from "@mui/material/Dialog";
import SendIcon from "@mui/icons-material/Send";
import ErrorIcon from "@mui/icons-material/Error";
import { Divider, MenuItem, CircularProgress } from "@mui/material";

import { getDropboxUrlWithRetry } from "../../../DropboxFunctions";
import getJobName from "../../../GlobalFunctions/getJobName";

const EmailSelectorDialogV2 = ({ open, handleClose, jobData, clientData }) => {
  const [theme] = useAtom(themeAtom);
  const [currentStaffData] = useAtom(currentStaffDataAtom);

  // EmailModalV2 state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState("");
  const [baseTemplate, setBaseTemplate] = useState(""); // Template with placeholders
  const [emailSubject, setEmailSubject] = useState("");
  const [emailToRecipients, setEmailToRecipients] = useState([]);
  const [emailCcRecipients, setEmailCcRecipients] = useState([]);
  const [emailBccRecipients, setEmailBccRecipients] = useState([]);
  const [emailLinks, setEmailLinks] = useState(""); // Store generated links
  const [currentEmailType, setCurrentEmailType] = useState(""); // Track which email type is being sent

  // Storyboard Details Modal state
  const [storyboardDetailsModalOpen, setStoryboardDetailsModalOpen] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  // Template loading function
  const loadEmailTemplate = async (templateName) => {
    try {
      const response = await fetch(`/EmailTemplates/${templateName}.html`);
      if (!response.ok) {
        throw new Error(`Failed to load template: ${templateName}`);
      }
      return await response.text();
    } catch (error) {
      console.error("Error loading email template:", error);
      alert(`Unable to load ${templateName} template`);
      return "";
    }
  };

  // Helper function to add permanent labels to job (from original EmailModal.js)
  const addLabelToCardFB = async (jobData, labelId) => {
    if (!jobData?.id) return;

    const updatedJob = { ...jobData };

    if (updatedJob.idLabelsPermanent) {
      if (!updatedJob.idLabelsPermanent.includes(labelId)) {
        updatedJob.idLabelsPermanent.push(labelId);
      }
    } else {
      updatedJob.idLabelsPermanent = [labelId];
    }

    const noDupes = [...new Set(updatedJob.idLabelsPermanent)];
    updatedJob.idLabelsPermanent = noDupes;

    await updateDoc(doc(db, "JobData", jobData.id), {
      idLabelsPermanent: updatedJob.idLabelsPermanent,
    });
  };

  // Helper function to generate download links for templates
  const generateDownloadLink = (url, label) => {
    return `<a href="${url}" target="_blank" style="display: inline-block; padding: 12px 24px; background-color: #000000; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: bold; margin: 8px;">${label}</a>`;
  };

  // Helper function to get client supply email addresses in proper format
  const getClientSupplyEmails = () => {
    if (!clientData?.contacts) return [];
    return clientData.contacts
      .filter((contact) => contact.isSupply && contact.email)
      .map((contact) => ({ email: contact.email, name: contact.name || "" }));
  };

  // Helper function to process template variables
  const processTemplate = (template, subject, links) => {
    let processed = template;
    processed = processed.replace(/{{{subject}}}/g, subject);
    processed = processed.replace(/{{{links}}}/g, links);
    return processed;
  };

  // Handler for when subject changes in EmailModalV2
  const handleSubjectChange = (newSubject) => {
    if (baseTemplate && emailLinks) {
      const updatedTemplate = processTemplate(
        baseTemplate,
        newSubject,
        emailLinks
      );
      setEmailTemplate(updatedTemplate);
    }
  };

  // Setup email fields for Supply/COD according to old modal logic
  const setupSupplyEmailFields = () => {
    const subject = getJobName(jobData);

    // TO: Client supply contacts
    const toEmails = getClientSupplyEmails();

    // CC: accounts@imagesthatsell.com.au (unless Morrissey Media)
    const ccEmails =
      jobData.client !== "Morrissey Media"
        ? [{ email: "accounts@imagesthatsell.com.au" }]
        : [];

    // Reply-To: production@imagesthatsell.com.au
    const replyToEmails = [{ email: "production@imagesthatsell.com.au" }];

    return { subject, toEmails, ccEmails, replyToEmails };
  };

  // Setup email fields for Video according to old modal logic
  const setupVideoEmailFields = () => {
    const subject = getJobName(jobData);

    // TO: adam@imagesthatsell.com.au, bookings@imagesthatsell.com.au
    const toEmails = [
      { email: "adam@imagesthatsell.com.au" },
      { email: "bookings@imagesthatsell.com.au" },
    ];

    // CC: alex@imagesthatsell.com.au, production@imagesthatsell.com.au
    const ccEmails = [
      { email: "alex@imagesthatsell.com.au" },
      { email: "production@imagesthatsell.com.au" },
    ];

    // Reply-To: production@imagesthatsell.com.au
    const replyToEmails = [{ email: "production@imagesthatsell.com.au" }];

    return { subject, toEmails, ccEmails, replyToEmails };
  };

  const getVideoEmailLinks = async () => {
    setLoading(true);
    setLoadingMessage("Preparing Video Debrief Email");

    try {
      // Load template
      const template = await loadEmailTemplate("Video");
      if (!template) {
        setLoading(false);
        return;
      }

      // Generate Dropbox link
      setLoadingMessage("Getting Dropbox Link");
      const path = `/${jobData.client}/${jobData.jobNumber}-${jobData.client}${jobData.jobTypeShort}`;
      const dropboxUrl = await getDropboxUrlWithRetry(path);

      let processedTemplate = template;
      let downloadLink = "<p>Video files link will be provided separately.</p>";

      if (dropboxUrl) {
        downloadLink = generateDownloadLink(dropboxUrl, "Click Here");
      }

      // Get video data from job breakdown
      const videoBreakdown = jobData?.imageBreakdown?.find(
        (obj) =>
          obj.description?.includes("Videos") ||
          obj.description?.includes("Video")
      );
      const totalVideos = videoBreakdown?.quantityProcessed || 0;
      const editedVideos = videoBreakdown?.filesInPost || 0;
      const staffName =
        currentStaffData?.preferredName ||
        currentStaffData?.firstName ||
        "Staff";
      const jobName = getJobName(jobData);

      // Replace template variables
      processedTemplate = processedTemplate
        .replace(/{{{links}}}/g, downloadLink)
        .replace(/{{{videoCount}}}/g, totalVideos)
        .replace(/{{{editedVideos}}}/g, editedVideos)
        .replace(/{{{jobName}}}/g, jobName)
        .replace(/{{{staffName}}}/g, staffName);

      // Set up email fields using video modal logic
      const { subject, toEmails, ccEmails } =
        setupVideoEmailFields();

      // Store base template and links for dynamic updates
      setBaseTemplate(processedTemplate);
      setEmailLinks(downloadLink);

      // Set up email modal
      setEmailTemplate(processedTemplate);
      setEmailSubject(subject);
      setEmailToRecipients(toEmails);
      setEmailCcRecipients(ccEmails);
      setEmailBccRecipients([]);
      setCurrentEmailType("video");
      setEmailModalOpen(true);
    } catch (error) {
      console.error("Error preparing video debrief email:", error);
      alert("Error preparing video debrief email");
    }

    setLoading(false);
    handleClose();
  };

  const getSupplyEmailLinks = async () => {
    setLoading(true);
    setLoadingMessage("Preparing Supply Email");

    try {
      // Load template
      const template = await loadEmailTemplate("Supply");
      if (!template) {
        setLoading(false);
        return;
      }

      // Ensure Supply subfolder exists (silently in background)
      const basePath = `/${jobData.client}/${jobData.jobNumber}-${jobData.client}${jobData.jobTypeShort}`;
      const supplyFolderPath = `${basePath}/Supply`;

      try {
        await getDropboxUrlWithRetry(supplyFolderPath);
      } catch (error) {
        // Silent error handling - don't block email if folder creation fails
        console.log("Supply folder creation failed (non-critical):", error);
      }

      // Generate supply portal link (handles all Dropbox access dynamically)
      const supplyUrl =
        process.env.NODE_ENV === "production"
          ? `https://supply.imagesthatsell.com.au/${jobData.id}`
          : `https://localhost:3000/supply/${jobData.id}`;

      const downloadLink = generateDownloadLink(supplyUrl, "DOWNLOAD FILES");
      const processedTemplate = template.replace("{{{links}}}", downloadLink);

      // Set up email fields using old modal logic
      const { subject, toEmails, ccEmails } =
        setupSupplyEmailFields();

      // Store base template and links for dynamic updates
      setBaseTemplate(processedTemplate);
      setEmailLinks(downloadLink);

      // Process template with subject
      const finalTemplate = processTemplate(
        processedTemplate,
        subject,
        downloadLink
      );

      // Set up email modal
      setEmailTemplate(finalTemplate);
      setEmailSubject(subject);
      setEmailToRecipients(toEmails);
      setEmailCcRecipients(ccEmails);
      setEmailBccRecipients([]);
      setCurrentEmailType("supply");
      setEmailModalOpen(true);
    } catch (error) {
      console.error("Error preparing supply email:", error);
      alert("Error preparing supply email");
    }

    setLoading(false);
    handleClose();
  };

  const getCODEmailLinks = async () => {
    try {
      // Load template
      const template = await loadEmailTemplate("COD");
      if (!template) {
        return;
      }

      // Process COD template immediately (doesn't use links)
      const processedTemplate = template;

      // Set up email fields using old modal logic
      const { subject, toEmails, ccEmails } =
        setupSupplyEmailFields();

      // Store base template (no links needed for COD template)
      setBaseTemplate(processedTemplate);
      setEmailLinks(""); // No links for COD template

      // Process template with subject only
      const finalTemplate = processTemplate(processedTemplate, subject, "");

      // Set up email modal immediately
      setEmailTemplate(finalTemplate);
      setEmailSubject(subject);
      setEmailToRecipients(toEmails);
      setEmailCcRecipients(ccEmails);
      setEmailBccRecipients([]);
      setCurrentEmailType("cod");
      setEmailModalOpen(true);

      // Generate Dropbox link silently in background for future Supply email optimization
      // This pre-generates the link to make Supply emails faster later
      const path = `/${jobData.client}/${jobData.jobNumber}-${jobData.client}${jobData.jobTypeShort}`;
      getDropboxUrlWithRetry(path).catch((error) => {
        // Silent error handling - COD email works fine without the link
        console.log("Background link generation failed (non-critical):", error);
      });
    } catch (error) {
      console.error("Error preparing COD email:", error);
      alert("Error preparing COD email");
    }

    handleClose();
  };

  const lifeStoryboardButtons = [
    {
      icon: <SendIcon />,
      name: "Storyboards",
      click: () => {
        setStoryboardDetailsModalOpen(true);
        handleClose();
      },
    },
  ];

  // Product jobs now use the same storyboards modal as life jobs
  const productStoryboardButtons = lifeStoryboardButtons;

  const videoEmailButtons = [
    {
      icon: <SendIcon />,
      name: "Video Debrief",
      click: async () => {
        await getVideoEmailLinks();
        handleClose();
      },
    },
  ];

  const supplyEmailButtons = [
    {
      icon: <SendIcon />,
      name: "Supply",
      click: async () => {
        await getSupplyEmailLinks();
        handleClose();
      },
    },
    {
      icon: <SendIcon />,
      name: "COD",
      click: async () => {
        await getCODEmailLinks();
        handleClose();
      },
    },
  ];

  let emailButtons = supplyEmailButtons;

  if (jobData?.jobTypeFull?.includes("-V")) {
    emailButtons = [...videoEmailButtons, ...emailButtons];
  } else if (jobData?.jobTypeFull?.includes("-P")) {
    emailButtons = [...productStoryboardButtons, ...emailButtons];
  } else if (jobData?.jobTypeFull?.includes("-L")) {
    emailButtons = [...lifeStoryboardButtons, ...emailButtons];
  }

  // If no job data, show message
  if (!jobData) {
    emailButtons = [
      {
        icon: <ErrorIcon />,
        name: "No job data available",
        click: () => {
          alert("Job data is required for email functionality.");
        },
      },
    ];
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              backgroundColor: theme === "dark" ? "#2c2c2c" : "#ffffff",
              color: theme === "dark" ? "#ffffff" : "#000000",
            },
          },
        }}
      >
        <div
          style={{
            width: 400,
            padding: "15px 15px 15px 15px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: theme === "dark" ? "#2c2c2c" : "#ffffff",
            color: theme === "dark" ? "#ffffff" : "#000000",
          }}
        >
          {loading ? (
            <div style={{ padding: "40px", textAlign: "center" }}>
              <CircularProgress
                style={{ color: theme === "dark" ? "#90caf9" : "#1976d2" }}
              />
              <div
                style={{
                  marginTop: "16px",
                  fontWeight: "bold",
                  color: theme === "dark" ? "#ffffff" : "#000000",
                }}
              >
                {loadingMessage}
              </div>
            </div>
          ) : (
            <>
              {emailButtons.map((button, index) => {
                return (
                  <span key={`emailItem_${index}`} style={{ width: 400 }}>
                    <MenuItem
                      onClick={button.click}
                      sx={{
                        color: theme === "dark" ? "#ffffff" : "#000000",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark" ? "#404040" : "#f5f5f5",
                        },
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ marginLeft: 10, marginRight: 25 }}>
                          {button.name}
                        </div>
                        <div
                          style={{
                            marginRight: 10,
                            marginTop: 0,
                            color: theme === "dark" ? "#90caf9" : "#1976d2",
                          }}
                        >
                          {button.icon}
                        </div>
                      </div>
                    </MenuItem>
                    {index !== emailButtons.length - 1 ? (
                      <Divider
                        sx={{
                          borderColor: theme === "dark" ? "#404040" : "#e0e0e0",
                        }}
                      />
                    ) : null}
                  </span>
                );
              })}
            </>
          )}
        </div>
      </Dialog>

      <EmailModalV2
        open={emailModalOpen}
        onClose={() => {
          setEmailModalOpen(false);
          // Reset template states
          setBaseTemplate("");
          setEmailLinks("");
          setCurrentEmailType("");
        }}
        initialTo={emailToRecipients}
        initialCc={emailCcRecipients}
        initialBcc={emailBccRecipients}
        template={emailTemplate}
        subject={emailSubject}
        fromEmail="production@imagesthatsell.com.au"
        onSubjectChange={handleSubjectChange}
        onSend={async (emailData) => {
          try {
            // Import Firebase functions
            const { httpsCallable } = await import("firebase/functions");
            const { functions } = await import("../../../firebaseConfig");

            const sendEmail = httpsCallable(functions, "sendEmail");

            // Send email via Firebase function
            await sendEmail({
              emailBody: emailData.htmlContent,
              fromEmail: "production@imagesthatsell.com.au",
              toEmails: emailData.to,
              ccEmails: emailData.cc,
              bccEmails: emailData.bcc,
              replyToEmails: emailData.replyTo || [],
              subject: emailData.subject,
              attachments: emailData.attachments || [],
            });

            console.log("Email sent successfully");

            // Add appropriate permanent label based on email type
            if (currentEmailType === "supply") {
              await addLabelToCardFB(jobData, "5e6881d6af988c41f2bf6e12");
            } else if (currentEmailType === "cod") {
              await addLabelToCardFB(jobData, "5f0e8644f6ee37295e76981e");
            }

            setEmailModalOpen(false);
            // Reset template states
            setBaseTemplate("");
            setEmailLinks("");
            setCurrentEmailType("");
          } catch (error) {
            console.error("Error sending email:", error);
            alert("Failed to send email. Please try again.");
          }
        }}
      />

      <StoryboardDetailsModal
        open={storyboardDetailsModalOpen}
        onClose={() => setStoryboardDetailsModalOpen(false)}
        jobData={jobData}
        clientData={clientData}
      />
    </>
  );
};

export default EmailSelectorDialogV2;
